import classNames from 'classnames';

import NextImage from '@com/_core/NextImage';
import Icon from '@utilities/icon';
import { Button } from '@com/_atoms/NewButton';
import { colors } from '@configs/Theme';
import { verticalDoctorCardTexts } from '@constant/home';

type NobatDoctorCardProps = {
  className?: string;
  imageUrl: string;
  star: number;
  title: string;
  subtitle: string;
  cities?: string[];
  appointmentCount?: number | null;
  onClick?: () => void;
  isLoading?: boolean;
  buttonText?: string;
};

const NobatDoctorCard = ({
  className,
  imageUrl,
  star,
  title,
  subtitle,
  cities,
  appointmentCount,
  onClick,
  isLoading,
  buttonText = verticalDoctorCardTexts.inPersonBooking,
}: NobatDoctorCardProps) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      onClick?.();
    }
  };

  const locationLabel = cities?.length ? cities.join(' - ') : null;

  return (
    <div
      className={classNames(
        className,
        'w-[152px] min-h-[226px] flex flex-col rounded-xl border border-border-primary/20 bg-surface-primary overflow-hidden shadow-fab',
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="relative h-[56px] bg-surface-tertiary px-2 pt-2">
        <div className="flex items-start justify-between gap-1">
          {appointmentCount && (
            <div className="flex items-center gap-x-1 rounded-full bg-surface-primary px-1 py-0.5">
              <Icon
                name="CircleCheckFill"
                width={0.75}
                height={0.75}
                fill={colors.blue[500]}
              />
              <span className="text-Label/2xSmall text-content-secondary whitespace-nowrap">
                {`${appointmentCount} نوبت`}
              </span>
            </div>
          )}

          <div className="flex items-center gap-x-0.5 rounded-full bg-surface-primary px-1 py-0.5">
            <Icon
              name="StarFill"
              width={0.75}
              height={0.75}
              fill={colors.yellow[400]}
            />
            <span className="text-Label/2xSmall text-content-primary">
              {star}
            </span>
          </div>
        </div>
      </div>

      <div className="relative -mt-6 flex justify-center">
        <div className="h-14 w-14 overflow-hidden rounded-full border-2 border-surface-primary bg-surface-tertiary">
          <NextImage src={imageUrl} alt="doctor" width={64} height={64} />
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center gap-y-1 px-3 pb-3 pt-2">
        {locationLabel && (
          <div className="flex items-center gap-x-1">
            <Icon
              name="PinCircle"
              width={0.875}
              height={0.875}
              fill={colors.gray[500]}
            />
            <span className="text-Label/xSmall text-content-primary line-clamp-1">
              {locationLabel}
            </span>
          </div>
        )}

        <span className="max-w-full text-center text-Label/Small text-content-primary truncate">
          {title}
        </span>

        <span className="max-w-full text-center text-Body/xSmall text-content-secondary truncate">
          {subtitle}
        </span>

        <div className="w-full mt-auto">
          <Button
            variant="primary"
            size="small"
            className="!h-10 w-full !text-Label/Small !rounded-full !bg-content-primary !border-content-primary"
            onClick={(event) => {
              event.stopPropagation();
              onClick?.();
            }}
            isLoading={isLoading}
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NobatDoctorCard;
